import styled, { css } from 'styled-components';
import { Dialog, Divider, Typography, ListItem, ListItemIcon, ListItemText, Chip } from '@mic3/platform-ui';
import Icon from 'app/components/atoms/Icon/Icon';

export const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        box-sizing: border-box;
        padding: 20px;
        background-color: ${({ theme }) => theme.material.colors.background.paper};
        min-width: 600px;
        max-width: 800px;
        width: 100%;
        
        @media (max-width: 768px) {
            min-width: unset;
            width: calc(100vw - 32px);
            max-width: calc(100vw - 32px);
            padding: 12px;
            margin: 16px;
        }
        
        @media (max-width: 480px) {
            width: 100vw;
            max-width: 100vw;
            height: 100vh;
            max-height: 100vh;
            margin: 0;
            padding: 8px;
            padding-top: 20px;
            border-radius: 0;
        }
    }
    *::-webkit-scrollbar {
        width: ${({ theme }) => theme.material.colors.scrollbar.width} !important;
        height: ${({ theme }) => theme.material.colors.scrollbar.height} !important;
    }
    *::-webkit-scrollbar-thumb {
        border-radius: ${({ theme }) => theme.material.colors.scrollbar.thumb.radius};
        box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
        background-color: ${({ theme }) => theme.material.colors.scrollbar.thumb.background};
        min-height: 65px;
    }
     *::-webkit-scrollbar-thumb:hover {
        background-color: ${({ theme }) => theme.material.colors.scrollbar.thumb.hover};
    }

    *::-webkit-scrollbar-thumb:active {
        background-color: ${({ theme }) => theme.material.colors.scrollbar.thumb.active};
    }    
    *::-webkit-scrollbar-track {
       box-shadow: ${({ theme }) => theme.material.colors.scrollbar.track.boxShadow};
        border-radius: 0;
        background-color: ${({ theme }) => theme.material.colors.scrollbar.track.background};
    }
     *::-webkit-scrollbar-track:hover {
        background-color: ${({ theme }) => theme.material.colors.scrollbar.track.hover};
    }
    
     *::-webkit-scrollbar-track:active {
        background-color: ${({ theme }) => theme.material.colors.scrollbar.track.active};
    }
`;

export const SearchInputContainer = styled.div`
    padding: 0;
`;

export const SearchInputWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 12px 12px 12px 0;
    background-color: transparent;
    border-bottom: 2px solid ${({ theme }) => theme.material.colors.border.main};
    min-height: 56px;
    position: relative;
    
    @media (max-width: 480px) {
        padding: 8px;
        gap: 6px;
        min-height: 48px;
    }
    
    &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 50%;
        width: 0;
        height: 2px;
        background-color: ${({ theme }) => theme.material.colors.primary.main};
        transition: width 0.3s ease-out, left 0.3s ease-out;
    }
    
    &:focus-within::after {
        width: 100%;
        left: 0;
    }
`;

export const SearchTokensContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex: 1;
`;

export const SearchTokenGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const baseChipStyles = css`
    color: ${({ theme }) => theme.material.colors.text.primary};
    background-color: ${({ theme }) => theme.material.colors.background.chips} !important;
    font-size: 12px;
    font-weight: 400;
    height: 28px;
    border-radius: 6px;
    
    @media (max-width: 480px) {
        font-size: 12px;
        height: 26px;
    }
`;

const standardLabelPadding = `
    .MuiChip-label {
        padding: 0 10px;
    }
`;

const deleteableLabelPadding = `
    .MuiChip-label {
        padding-left: 10px;
        padding-right: 4px;
    }
`;

const deleteIconStyles = `
    .MuiChip-deleteIcon {
        color: ${({ theme }) => theme.material.colors.text.primary};
        font-size: 16px;
        margin: 0 2px;
        opacity: 0.6;
        &:hover {
            opacity: 1;
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: 7px;
        }
    }
`;

export const FilterChip = styled(Chip)`
    && {
        ${baseChipStyles}
        
        ${standardLabelPadding}
        
        .MuiChip-deleteIcon {
            display: none;
        }
    }
`;

export const OperatorChip = styled(Chip)`
    && {
        ${baseChipStyles}
        min-width: 40px;
        
        ${standardLabelPadding}
        
        .MuiChip-deleteIcon {
            display: none;
        }
    }
`;

export const ValueChip = styled(Chip)`
    && {
        ${baseChipStyles}
        padding-right: 4px;
        
        ${deleteableLabelPadding}
        ${deleteIconStyles}
    }
`;

export const BuildingTokenGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const BuildingChip = styled(Chip)`
    && {
        ${baseChipStyles}
        padding-right: 4px;
        
        ${deleteableLabelPadding}
        ${deleteIconStyles}
    }
`;

export const SearchIconAdornment = styled.div`
    display: flex;
    align-items: center;
    margin-right: 8px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

export const MobileCloseButton = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    margin-left: 8px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: ${({ theme }) => theme.material.colors.background.hover};
        color: ${({ theme }) => theme.material.colors.text.primary};
    }
    
    &:active {
        background-color: ${({ theme }) => theme.material.colors.background.active || theme.material.colors.background.hover};
        transform: scale(0.95);
    }
    
    @media (max-width: 480px) {
        display: flex;
        min-width: 40px;
        min-height: 40px;
    }
`;

export const StyledInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.material.colors.text.primary};
    font-size: 14px;
    flex: 1;
    min-width: 150px;
    padding: 0;
    
    @media (max-width: 480px) {
        min-width: 100px;
        font-size: 16px; // Prevents iOS zoom on focus
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.material.colors.text.secondary};
    }
`;

export const SearchContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 600px;
    overflow-x: hidden;
    overflow-y: ${({ $hasResults }) => ($hasResults ? 'hidden' : 'auto')};
    position: relative;
    
    @media (max-width: 768px) {
        height: 500px;
    }
    
    @media (max-width: 480px) {
        height: calc(100vh - 80px);
    }
`;

export const ResultsContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
    opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    position: ${({ $isHidden }) => ($isHidden ? 'absolute' : 'relative')};
    width: 100%;
    height: 100%;
    pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

export const FilterOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.material.colors.background.paper};
    z-index: 10;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
    opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
`;

export const SectionTitle = styled(Typography)`
    && {
        padding: 16px 0 8px 0;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        font-weight: 500;
        
        @media (max-width: 480px) {
            padding: 12px 0 6px 0;
            font-size: 12px;
        }
    }
`;

export const StyledDivider = styled(Divider)`
    margin: 8px 0 !important;
`;

export const StyledListItem = styled(ListItem)`
    && {
        padding: 8px;
        cursor: pointer;
        border-radius: 4px;
        
        ${({ $highlighted, theme }) => $highlighted && `
            background-color: ${theme.material.colors.primary.main}33 !important;
            border: 1px solid ${theme.material.colors.primary.main}66;
        `}
        
        &:hover {
            background-color: ${({ theme }) => theme.material.colors.background.hover};
        }
        
        @media (max-width: 480px) {
            padding: 6px 0;
        }
    }
`;

export const StyledListItemIcon = styled(ListItemIcon)`
    && {
        min-width: 36px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
    }
`;

export const StyledListItemText = styled(ListItemText)`
    && {
        .MuiListItemText-primary {
            color: ${({ theme }) => theme.material.colors.text.primary};
            font-size: 14px;
        }
        
        .MuiListItemText-secondary {
            font-size: 12px;
        }
        
        @media (max-width: 480px) {
            .MuiListItemText-primary {
                font-size: 13px;
            }
            
            .MuiListItemText-secondary {
                font-size: 11px;
            }
        }
    }
`;

export const SearchIcon = styled(Icon)`
    &:before {
        font-size: 120px !important;
        color: rgba(255, 255, 255, 0.24);
        @media (max-width: 480px) {
            font-size: 80px !important;
        }
    }
`;

export const NoResults = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    text-align: center;
    height: 100%;
    min-height: 400px;
    
    /* Only apply negative margin when paragraph follows SearchIcon */
    ${SearchIcon} + p {
        margin-top: -1.5rem;
    }
    
    /* Regular paragraph spacing for loader case */
    p {
        margin: 0;
        margin-top: 16px;
    }
    
    @media (max-width: 480px) {
        padding: 32px 12px;
        min-height: 300px;
        
        ${SearchIcon} + p {
            margin-top: 8px;
        }
        
        p {
            margin-top: 12px;
        }
    }
`;

export const ResultsList = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 8px 0;
    overflow-y: auto;
    overflow-x: hidden;
`;

export const ResultItem = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    padding-left: 8px;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.2s ease;
    gap: 12px;
    
    &:hover {
        background-color: ${({ theme }) => theme.material.colors.background.hover};
    }
    
    @media (max-width: 480px) {
        padding: 10px 12px;
        gap: 10px;
    }
`;

export const ResultIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    @media (max-width: 480px) {
        min-width: 36px;
        height: 36px;
    }
`;

export const ResultContent = styled.div`
    flex: 1;
    min-width: 0;
`;

export const ResultTitle = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.material.colors.text.primary};
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    @media (max-width: 480px) {
        font-size: 15px;
    }
`;

export const ResultMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

export const ResultMetaItem = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    display: flex;
    align-items: center;
    gap: 4px;
    
    @media (max-width: 480px) {
        font-size: 11px;
    }
`;

export const ResultType = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    background-color: ${({ theme }) => theme.material.colors.background.hover};
    padding: 2px 8px;
    border-radius: 12px;
    
    @media (max-width: 480px) {
        font-size: 11px;
        padding: 2px 6px;
    }
`;

export const ResultDescription = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    
    @media (max-width: 480px) {
        font-size: 12px;
    }
`;

export const LoadMoreButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    margin-top: 16px;
    background-color: ${({ theme }) => theme.material.colors.background.default};
    border: 1px solid ${({ theme }) => theme.material.colors.divider};
    border-radius: 8px;
    color: ${({ theme }) => theme.material.colors.primary.main};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.material.colors.background.hover};
        border-color: ${({ theme }) => theme.material.colors.primary.main};
    }
    
    &:active:not(:disabled) {
        transform: scale(0.98);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    
    @media (max-width: 480px) {
        padding: 14px 16px;
        font-size: 15px;
        min-height: 48px;
    }
`;

export const LoadMoreButtonIcon = styled(Icon).attrs(({ theme }) => ({
    hexColor: theme.material.colors.text.secondary,
}))``;

export const LoadMoreButtonText = styled.span`
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;


